// v50.122 — Wrapped-DEK crypto wallet.
//
// Real multi-party encryption-at-rest, distinct from the v49 LABOURCORE_CRYPTO
// module (which uses a per-session key and can't be shared between users).
//
// Key hierarchy:
//   • Per-user passphrase
//      → PBKDF2-SHA-256 (200k iterations) + per-user salt
//        → KEK (key-encryption key)
//   • Per-company DEK (random AES-GCM-256), generated at wallet-setup time
//        → stored wrapped with KEK in crypto_user_keys.wrapped_dek
//          → unwrapped client-side after user enters passphrase
//   • Per-row or per-record content key (random AES-GCM-256)
//        → encrypts the actual field values
//          → wrapped with the company DEK and stored on the row
//
// Why per-record content keys instead of encrypting directly with the
// company DEK: it lets us "share" a row with multiple companies (e.g. a
// dispute that needs to be readable by both coord + partner) by wrapping
// the same content key with each side's DEK. The company DEK stays per-
// company; the per-record key bridges across.

const PBKDF2_ITERATIONS = 200_000;
const SALT_BYTES   = 16;
const NONCE_BYTES  = 12;   /* AES-GCM standard nonce length */

const HAS_CRYPTO = typeof crypto !== "undefined" && !!crypto.subtle;

if (!HAS_CRYPTO && typeof window !== "undefined") {
  console.warn("[wallet] WebCrypto unavailable — crypto wallet disabled");
}

/* ── Base64 / byte helpers ─────────────────────────────────────────────── */

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

export function base64ToBytes(s: string): Uint8Array {
  const binary = atob(s);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export function randomBytes(n: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(n));
}

/* ── Key derivation + generation ───────────────────────────────────────── */

/* Generate a fresh random AES-GCM-256 key, exportable so it can be wrapped. */
export async function generateRandomKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true /* extractable */,
    ["encrypt", "decrypt"],
  );
}

/* Derive a KEK from a passphrase + salt. Iteration count + salt are
   stored alongside the wrapped DEK so future logins can re-derive. */
export async function deriveKekFromPassphrase(
  passphrase: string,
  salt:       Uint8Array,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<CryptoKey> {
  if (!passphrase || passphrase.length < 8) throw new Error("Passphrase must be at least 8 characters");
  const baseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase) as BufferSource,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true,  /* extractable so we can wrap with it */
    ["wrapKey", "unwrapKey", "encrypt", "decrypt"],
  );
}

/* ── Wrap / unwrap symmetric keys ──────────────────────────────────────── */

export interface WrappedKey {
  wrapped: Uint8Array;
  nonce:   Uint8Array;
}

/* AES-GCM wrap of one CryptoKey by another. Returns wrapped bytes +
   nonce (caller stores both). */
export async function wrapKey(targetKey: CryptoKey, wrapKey: CryptoKey): Promise<WrappedKey> {
  const nonce = randomBytes(NONCE_BYTES);
  const wrapped = await crypto.subtle.wrapKey(
    "raw",
    targetKey,
    wrapKey,
    { name: "AES-GCM", iv: nonce as BufferSource },
  );
  return { wrapped: new Uint8Array(wrapped), nonce };
}

export async function unwrapKey(
  wrapped:    Uint8Array,
  nonce:      Uint8Array,
  unwrapWith: CryptoKey,
  usages:     KeyUsage[] = ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
): Promise<CryptoKey> {
  return crypto.subtle.unwrapKey(
    "raw",
    wrapped as BufferSource,
    unwrapWith,
    { name: "AES-GCM", iv: nonce as BufferSource },
    { name: "AES-GCM", length: 256 },
    true,
    usages,
  );
}

/* ── Encrypt / decrypt arbitrary plaintext ─────────────────────────────── */

export interface Ciphertext {
  ct:    Uint8Array;
  nonce: Uint8Array;
}

export async function encryptString(plaintext: string, key: CryptoKey): Promise<Ciphertext> {
  const nonce = randomBytes(NONCE_BYTES);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    new TextEncoder().encode(plaintext) as BufferSource,
  );
  return { ct: new Uint8Array(ct), nonce };
}

export async function decryptString(ct: Uint8Array, nonce: Uint8Array, key: CryptoKey): Promise<string> {
  const plainBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce as BufferSource },
    key,
    ct as BufferSource,
  );
  return new TextDecoder().decode(plainBuf);
}

/* ── Wallet setup + unlock helpers ─────────────────────────────────────── */

export interface WalletSetupResult {
  /* Random per-user salt for PBKDF2 — stored alongside the wrapped DEK. */
  salt:        Uint8Array;
  /* Iteration count used — stored so future logins can re-derive even if
     we bump the default later. */
  iterations:  number;
  /* The user's KEK, retained in caller memory for further wrapping. */
  kek:         CryptoKey;
  /* The freshly-generated company DEK, retained in caller memory for
     subsequent encrypt/decrypt operations this session. */
  dek:         CryptoKey;
  /* DEK wrapped with the KEK, ready to insert into crypto_user_keys. */
  wrappedDek:  WrappedKey;
}

/* Brand-new wallet setup for a user who has never had a wallet for this
   company before. Generates a new company DEK + salt + wrapped DEK. */
export async function setupNewCompanyDek(passphrase: string): Promise<WalletSetupResult> {
  const salt = randomBytes(SALT_BYTES);
  const kek  = await deriveKekFromPassphrase(passphrase, salt);
  const dek  = await generateRandomKey();
  const wrappedDek = await wrapKey(dek, kek);
  return { salt, iterations: PBKDF2_ITERATIONS, kek, dek, wrappedDek };
}

/* Unlock a wallet using the user's existing wrapped DEK + salt. */
export async function unlockExistingWallet(args: {
  passphrase: string;
  salt:       Uint8Array;
  iterations: number;
  wrappedDek: Uint8Array;
  wrapNonce:  Uint8Array;
}): Promise<{ kek: CryptoKey; dek: CryptoKey }> {
  const kek = await deriveKekFromPassphrase(args.passphrase, args.salt, args.iterations);
  let dek: CryptoKey;
  try {
    dek = await unwrapKey(args.wrappedDek, args.wrapNonce, kek);
  } catch (e) {
    /* AES-GCM unwrap fails with OperationError on a wrong key — translate
       to a clearer error message for the UI. */
    throw new Error("Wrong passphrase — couldn't unlock wallet");
  }
  return { kek, dek };
}

/* Re-wrap an existing DEK with a different KEK (for passphrase rotation
   or for granting a new member access to the same company DEK). */
export async function rewrapDekForNewKek(dek: CryptoKey, newKek: CryptoKey): Promise<WrappedKey> {
  return wrapKey(dek, newKek);
}
